import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import AccessibilityPanel from "../components/AccessibilityPanel";
import { usePageTitle } from "../components/AppLayout";
import { Btn, Field, G_DARK, Input, PasswordInput, globalStyles } from "../components/Shared";

// The sign-in page offers what GET /api/auth/config says is on (both are set by an admin in
// Management -> Sign-in, not by environment variables):
//   - "Sign in with <name>" while Keycloak is enabled and fully configured;
//   - the password form while password sign-in (local accounts) is on: "E-mail" and "Password",
//     sent as {email, password} (the e-mail is the sign-in identifier; only the bootstrap admin
//     types its name, e.g. "admin", in the same field), with "Forgot password?" (/forgot-password);
//   - the development form while UNIO_DEV_LOGIN is on (dev/test only).
// A refusal from the Keycloak callback arrives as ?oidc_error=<sentence>, shown verbatim; after
// one, the next Keycloak sign-in asks for credentials (prompt=login) rather than replaying the
// SSO session that was just refused, and a way to sign out of Keycloak is offered — except under
// ?oidc_logout=failed, where that same click would fail the same way. Pattern from data-portal.
// Under the sign-in box (after the sign-in controls in the keyboard order), the links an admin
// sets in Management -> Sign-in page links (config.links: [{title, url, description}], task
// 2026-10-22): rendered as text, each opening in a new tab without an opener or referrer; no
// links, no heading.

const ERROR_BOX = { padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)",
                    border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 };

// Only a path on this site (no "//host", no backslash, no control characters).
export function safeNext(next) {
  if (typeof next !== "string") return "/projects";
  // eslint-disable-next-line no-control-regex
  if (!/^\/(?![/\\])/.test(next) || /[\\\u0000-\u001f\u007f]/.test(next)) return "/projects";
  return next;
}

function HelpLinks({ links }) {
  if (!Array.isArray(links) || links.length === 0) return null;
  return (
    <section aria-labelledby="login-help-links" style={{ marginTop: 24 }}>
      <h2 id="login-help-links" style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>
        Help and documentation
      </h2>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        {links.map((l, i) => (
          <li key={i} style={{ minWidth: 0, position: "relative" }}>
            <a href={l.url} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: 14, fontWeight: 600, color: G_DARK, overflowWrap: "anywhere" }}>
              {l.title}<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap" }}> (opens in a new tab)</span>
            </a>
            {l.description && (
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "#4b5563", overflowWrap: "anywhere", whiteSpace: "pre-line" }}>
                {l.description}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function LoginPage() {
  usePageTitle("Sign in");
  const { user, login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const oidcError = params.get("oidc_error");
  const offerOidcSignOut = !!oidcError && params.get("oidc_logout") !== "failed";
  // Until the config answers, the password form is shown (it is the way in a fresh portal has).
  const [config, setConfig] = useState({ oidc_enabled: false, oidc_display_name: "", local_login: true,
                                         dev_login: false, links: [], loaded: false });
  const [form, setForm] = useState({ email: "", password: "" });
  const [devForm, setDevForm] = useState({ username: "", password: "" });
  const [error, setError] = useState(null);
  const [devError, setDevError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [a11yOpen, setA11yOpen] = useState(false);

  useEffect(() => {
    if (user) { navigate(next, { replace: true }); return undefined; }
    let live = true;
    api.get("/auth/config")
      .then(c => { if (live) setConfig({ ...c, loaded: true }); })
      .catch(() => { if (live) setConfig(c => ({ ...c, loaded: true })); });
    return () => { live = false; };
  }, [user, navigate, next]);

  const oidcName = config.oidc_display_name || "Keycloak";
  const showPassword = config.local_login !== false;

  // A full navigation, and a replace: an /api/auth/oidc/* URL left in the history is one Back
  // away from a sign-in Keycloak answers silently from a live SSO session.
  const signIn = () =>
    window.location.replace(`/api/auth/oidc/login?next=${encodeURIComponent(next)}`
                            + (oidcError ? "&prompt=login" : ""));
  const signOutOfOidc = () => window.location.replace("/api/auth/oidc/logout");

  const set = (setter, k) => e => setter(f => ({ ...f, [k]: e.target.value }));

  const passwordSignIn = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(form.email.trim(), form.password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const devSignIn = async e => {
    e.preventDefault();
    setBusy(true);
    setDevError(null);
    try {
      const r = await api.post("/auth/dev/login", devForm);
      await loginWithToken(r.access_token);
      navigate(next, { replace: true });
    } catch (err) {
      setDevError(err.message);
      setBusy(false);
    }
  };

  const nothingOn = config.loaded && !config.oidc_enabled && !showPassword && !config.dev_login;

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap" rel="stylesheet" />
      <style>{globalStyles}</style>
      <main style={{ maxWidth: 400, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img src="/csm_binf.png" alt="BOKU Core Facility Bioinformatics" style={{ height: 60, borderRadius: 12, marginBottom: 16 }} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a1a" }}>Unio</h1>
          <p style={{ margin: "6px 0 0", fontSize: 14, color: "#4b5563" }}>BOKU Core Facility Bioinformatics</p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 16, padding: 28, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: 14 }}>
          {oidcError && <div role="alert" style={ERROR_BOX}>{oidcError}</div>}
          {offerOidcSignOut && config.oidc_enabled && (
            <button type="button" onClick={signOutOfOidc}
                    style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit",
                             fontSize: 13, color: G_DARK, fontWeight: 500, textDecoration: "underline", textAlign: "center" }}>
              Sign out of {oidcName} / use a different account
            </button>
          )}

          {config.oidc_enabled && (
            <>
              <Btn onClick={signIn} style={{ justifyContent: "center", width: "100%" }}>Sign in with {oidcName}</Btn>
              <p style={{ margin: 0, fontSize: 12, color: "#4b5563", textAlign: "center" }}>
                Use your institutional account. A project's curator adds a new account to the project.
              </p>
            </>
          )}

          {showPassword && (
            <form onSubmit={passwordSignIn} aria-label="Password sign-in"
                  style={{ display: "flex", flexDirection: "column", gap: 12, ...(config.oidc_enabled ? { borderTop: "1px solid #e5e7eb", paddingTop: 16 } : {}) }}>
              {config.oidc_enabled && (
                <p style={{ margin: 0, fontSize: 12, color: "#4b5563" }}>Or with a portal account and password:</p>
              )}
              {/* type="text", not "email": the bootstrap admin signs in with its name */}
              <Field label="E-mail">
                <Input type="text" inputMode="email" autoComplete="username" spellCheck={false}
                       value={form.email} onChange={set(setForm, "email")} />
              </Field>
              <Field label="Password">
                <PasswordInput autoComplete="current-password" value={form.password} onChange={set(setForm, "password")} />
              </Field>
              {error && <div role="alert" style={ERROR_BOX}>{error}</div>}
              <Btn type="submit" disabled={busy || !form.email.trim() || !form.password}
                   style={{ justifyContent: "center", width: "100%" }}>
                {busy ? "Signing in…" : "Sign in"}
              </Btn>
              {config.loaded && config.local_login && (
                <Link to="/forgot-password" style={{ fontSize: 13, color: G_DARK, fontWeight: 500, textAlign: "center" }}>
                  Forgot password?
                </Link>
              )}
            </form>
          )}

          {nothingOn && (
            <p role="status" style={{ margin: 0, fontSize: 13, color: "#374151" }}>
              No way to sign in is switched on. An administrator turns password sign-in back on with
              {" "}<code>python -m unio.cli local-login on</code>.
            </p>
          )}

          {config.dev_login && (
            <form onSubmit={devSignIn} aria-label="Development sign-in" style={{ borderTop: "1px solid #e5e7eb", paddingTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ margin: 0, fontSize: 12, color: "#92400e" }}>
                Development sign-in through the dev Keycloak (UNIO_DEV_LOGIN is on). Never enabled in a deployment.
              </p>
              <Field label="Keycloak username">
                <Input autoComplete="off" value={devForm.username} onChange={set(setDevForm, "username")} />
              </Field>
              <Field label="Keycloak password">
                <Input type="password" autoComplete="off" value={devForm.password} onChange={set(setDevForm, "password")} />
              </Field>
              {devError && <div role="alert" style={{ color: "#b91c1c", fontSize: 13 }}>{devError}</div>}
              <Btn type="submit" variant="secondary" disabled={busy}>Sign in (dev)</Btn>
            </form>
          )}
        </div>

        <HelpLinks links={config.links} />

        <p style={{ textAlign: "center", marginTop: 20, fontSize: 13 }}>
          <button type="button" onClick={() => setA11yOpen(true)} style={{ background: "none", border: "none", color: "var(--dp-accent-dark)", cursor: "pointer", fontSize: 13, fontFamily: "inherit", textDecoration: "underline" }}>
            Accessibility settings
          </button>
        </p>
      </main>
      {a11yOpen && <AccessibilityPanel onClose={() => setA11yOpen(false)} statementPath="/accessibility" />}
    </div>
  );
}
